import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, MapPin, Car } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import SectionReveal from "@/components/SectionReveal";
import LeadForm from "@/components/LeadForm";
import { LandingHero, TestimonialBand, FaqSection, CrossSell, FinalCta, localBusinessJsonLd, ADDRESS } from "@/components/LandingBits";
import { trackCta, trackBeginCheckout } from "@/lib/analytics";

const PATH = "/gym-windermere";
const JOIN = "/memberships/kore-access";

const EQUIPMENT = [
  "Squat racks", "Free weights & barbells", "Dumbbells", "Kettlebells", "Cable equipment",
  "Rowing machines", "SkiErgs", "Treadmills", "Functional training area", "Cardio equipment",
];

const WHO = [
  "You already know how to train.",
  "You don't need a coach beside you.",
  "You want somewhere quieter and friendlier than a commercial gym.",
  "You value quality equipment and a good atmosphere.",
  "You want somewhere local to train consistently.",
];

const GALLERY = [
  ["/gallery/gym-floor.webp", "KORE gym floor with turf strip and rig — Bowness-on-Windermere"],
  ["/gallery/gym-squat-rack.webp", "Squat rack and plates at KORE Wellbeing gym, Windermere"],
  ["/gallery/gym-cardio-1.webp", "Cardio equipment at KORE Wellbeing gym, Bowness"],
  ["/gallery/gym-cable-rack.webp", "Cable equipment and dumbbell rack at KORE Wellbeing"],
  ["/gallery/gym-bench.webp", "Bench and free weights at KORE Wellbeing gym"],
  ["/gallery/gym-reception.webp", "Reception at KORE Wellbeing, Rowan House"],
];

const FAQS = [
  { q: "Is KORE a normal gym?", a: "It's an independent gym in Bowness-on-Windermere with proper strength and cardio equipment. What's different is the atmosphere — quieter, friendlier and built for adults, not a big-chain floor." },
  { q: "Can I just use the gym without coaching?", a: "Yes. KORE Access is an open gym membership for people comfortable training independently. If you'd like structure and accountability, look at KORE Group Coaching instead." },
  { q: "Do you offer day passes?", a: "Yes — a gym day pass is £12 and gives you full access to the training floor and equipment during staffed hours. Get in touch to arrange one." },
  { q: "What are the opening hours?", a: "Open gym: Mon & Wed 09:00–18:00, Tue & Fri 09:00–14:00, Thu 11:00–18:00, Sat 08:30–14:00, Sun 08:30–12:00. Group coaching runs weekdays from 06:00." },
  { q: "Is there parking?", a: "Yes, there is parking at Rowan House on Beresford Road." },
  { q: "Where is the gym?", a: `${ADDRESS.street}, ${ADDRESS.locality}, ${ADDRESS.region} ${ADDRESS.postcode} — a couple of minutes from the lake.` },
];

export default function GymWindermere() {
  const [tests, setTests] = useState([]);
  useEffect(() => { api.get("/testimonials").then((r) => setTests(r.data)).catch(() => {}); }, []);

  const joinClick = (loc) => () => {
    trackCta({ cta_name: "gym_join_access", cta_text: "Join KORE Access", destination_url: JOIN, cta_location: loc });
    trackBeginCheckout({ item_name: "KORE Access", membership_type: "access", value: 49, destination_url: JOIN });
  };
  const dayPassClick = (loc) => () => trackCta({ cta_name: "gym_day_pass", cta_text: "Get a Day Pass", destination_url: "/contact", cta_location: loc });

  return (
    <div data-testid="gym-page">
      <Seo
        title="Gym in Windermere & Bowness"
        description="A friendly independent gym in Bowness-on-Windermere for people who want quality equipment, a quieter environment and the freedom to train independently."
        path={PATH}
        jsonLd={{
          ...localBusinessJsonLd({ url: `https://korewellbeing.com${PATH}`, description: "Independent gym in Bowness-on-Windermere with open gym membership and day passes." }),
          openingHoursSpecification: [
            { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Wednesday"], opens: "09:00", closes: "18:00" },
            { "@type": "OpeningHoursSpecification", dayOfWeek: ["Tuesday", "Friday"], opens: "09:00", closes: "14:00" },
            { "@type": "OpeningHoursSpecification", dayOfWeek: "Thursday", opens: "11:00", closes: "18:00" },
            { "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: "08:30", closes: "14:00" },
            { "@type": "OpeningHoursSpecification", dayOfWeek: "Sunday", opens: "08:30", closes: "12:00" },
          ],
        }}
      />

      <LandingHero
        testId="gym-hero"
        eyebrow="KORE Access · Windermere & Bowness"
        h1="A Friendly Independent Gym in Windermere"
        sub="Everything you need to train well — without the crowds, intimidation or big-chain gym atmosphere."
        image="/gallery/hero-gym-wide.webp"
        imageAlt="KORE Wellbeing gym floor in Bowness-on-Windermere"
        primary={{ to: "#look-around", label: "Book a look around", onClick: () => trackCta({ cta_name: "gym_book_look", cta_text: "Book a look around", destination_url: "#look-around", cta_location: "hero" }) }}
        secondary={{ to: "/contact", label: "Get a Day Pass", onClick: dayPassClick("hero") }}
      />

      <SectionReveal as="section" data-testid="gym-intro" className="bg-kore-warm py-24 md:py-32">
        <div className="container-kore grid md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <span className="eyebrow">KORE Access</span>
            <h2 className="display-2 mt-4 text-kore-forest">Your gym. <span className="italic text-kore-gold">Your pace.</span></h2>
            <div className="mt-8 space-y-5 text-kore-ink/80 leading-relaxed text-[17px] max-w-xl">
              <p>KORE Access is our open gym membership for people who are comfortable training independently. Quality strength and cardio equipment, a calm, adult environment and a friendly welcome every time you walk in.</p>
              <p>It's a small independent gym in Bowness-on-Windermere — not a commercial chain. You'll recognise faces, never queue for a rack, and train alongside people who are there to look after themselves, not to perform.</p>
            </div>
          </div>
          <div className="md:col-span-5 md:col-start-8">
            <div data-testid="gym-pricing" className="bg-white rounded-3xl p-8 md:p-10 border border-kore-gold/25 shadow-soft">
              <div className="text-[11px] uppercase tracking-[0.22em] text-kore-gold">Membership options</div>
              <div className="mt-6 font-display text-5xl text-kore-forest font-light">£49<span className="text-base text-kore-ink/55 font-normal"> / month rolling</span></div>
              <div className="mt-3 font-display text-3xl text-kore-forest font-light">£39<span className="text-base text-kore-ink/55 font-normal"> / month on a 12-month plan</span></div>
              <div className="mt-6 pt-6 border-t border-kore-gold/20 flex items-baseline justify-between">
                <span className="text-sm text-kore-ink/70">Gym day pass</span>
                <span className="font-display text-2xl text-kore-forest">£12</span>
              </div>
              <Link to={JOIN} data-testid="gym-pricing-join" onClick={joinClick("pricing")} className="btn-primary w-full justify-center mt-8">Join KORE Access <ArrowRight size={14} /></Link>
              <Link to="/contact" data-testid="gym-pricing-daypass" onClick={dayPassClick("pricing")} className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-kore-forest border-b border-kore-gold pb-1">Get a Day Pass</Link>
            </div>
          </div>
        </div>
      </SectionReveal>

      <section data-testid="gym-gallery" className="bg-kore-warm pb-24 md:pb-32">
        <div className="container-kore grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {GALLERY.map(([src, alt]) => (
            <div key={src} className="rounded-3xl overflow-hidden aspect-[4/3] shadow-soft"><img src={src} alt={alt} loading="lazy" className="w-full h-full object-cover" /></div>
          ))}
        </div>
      </section>

      <SectionReveal as="section" data-testid="gym-equipment" className="bg-kore-forest text-kore-warm py-28 md:py-36">
        <div className="container-kore grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <span className="eyebrow text-kore-light">The floor</span>
            <h2 className="font-display font-light text-4xl sm:text-5xl mt-5 leading-[1.05]">Quality equipment. <span className="italic text-kore-light">Room to use it.</span></h2>
            <p className="mt-6 text-kore-warm/75 leading-relaxed">Strength, cardio and free weights — everything you need for a proper session, without the queue.</p>
          </div>
          <ul className="md:col-span-6 md:col-start-7 grid sm:grid-cols-2 gap-x-8 gap-y-4 text-[17px]">
            {EQUIPMENT.map((e) => <li key={e} className="border-b border-kore-warm/15 pb-3">{e}</li>)}
          </ul>
        </div>
      </SectionReveal>

      <SectionReveal as="section" data-testid="gym-who" className="bg-kore-warm py-24 md:py-32">
        <div className="container-kore grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <span className="eyebrow">Who KORE Access is for</span>
            <h2 className="display-2 mt-4 text-kore-forest">Comfortable training on your own?</h2>
            <ul className="mt-8 space-y-4">
              {WHO.map((w) => <li key={w} className="font-display text-2xl text-kore-forest leading-tight border-b border-kore-gold/20 pb-4">{w}</li>)}
            </ul>
          </div>
          <div className="md:col-span-5 md:col-start-8">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-lift">
              <img src="/gallery/gym-storage.webp" alt="Kettlebells and equipment storage at KORE Wellbeing gym" loading="lazy" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </SectionReveal>

      <SectionReveal as="section" data-testid="gym-visit" className="bg-kore-warm pb-24 md:pb-32">
        <div className="container-kore grid sm:grid-cols-3 gap-8 border-t border-kore-gold/20 pt-12">
          <div className="flex gap-3"><Clock size={18} className="text-kore-gold mt-1 shrink-0" /><div><div className="eyebrow">Opening hours</div><p className="mt-3 text-kore-ink/80 leading-relaxed">Mon & Wed 09:00–18:00<br />Tue & Fri 09:00–14:00 · Thu 11:00–18:00<br />Sat 08:30–14:00 · Sun 08:30–12:00</p></div></div>
          <div className="flex gap-3"><MapPin size={18} className="text-kore-gold mt-1 shrink-0" /><div><div className="eyebrow">Location</div><p className="mt-3 text-kore-ink/80 leading-relaxed">{ADDRESS.name}<br />{ADDRESS.street}<br />{ADDRESS.locality}, {ADDRESS.region} {ADDRESS.postcode}</p></div></div>
          <div className="flex gap-3"><Car size={18} className="text-kore-gold mt-1 shrink-0" /><div><div className="eyebrow">Parking</div><p className="mt-3 text-kore-ink/80 leading-relaxed">Parking available at Rowan House. A couple of minutes from the lake and Bowness town centre.</p></div></div>
        </div>
      </SectionReveal>

      <section id="look-around" data-testid="gym-look-around" className="bg-kore-forest text-kore-warm py-24 md:py-28 scroll-mt-20">
        <div className="container-kore grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <span className="eyebrow text-kore-light">Book a look around</span>
            <h2 className="font-display font-light text-4xl sm:text-5xl mt-5 leading-[1.05]">Come and see it <span className="italic text-kore-light">before you decide.</span></h2>
            <p className="mt-6 text-kore-warm/75 leading-relaxed">Ten minutes, no hard sell. Tell us when suits and we'll be in touch within 24 hours.</p>
          </div>
          <div className="md:col-span-7 rounded-3xl bg-kore-warm text-kore-ink p-8 md:p-10">
            <LeadForm funnel="access" submitLabel="Book a look around" testId="access-form" showGoal={false}
              extraFields={[{ key: "preferred_day", label: "Preferred day / time to visit", required: true, wide: true }]} />
          </div>
        </div>
      </section>

      <TestimonialBand testId="gym-testimonials" quotes={tests} />

      <FaqSection testId="gym-faq" h2="Popular questions from Windermere & Bowness locals." faqs={FAQS} />

      <CrossSell
        testId="gym-crosssell"
        eyebrow="Need more structure and accountability?"
        h2="Explore KORE Group Coaching."
        body="Two coached sessions a week, a structured plan and a small group of people 40+ who are there for the same reason you are. Our flagship membership."
        to="/group-personal-training-windermere"
        label="Explore Coaching"
        onClick={() => trackCta({ cta_name: "gym_to_coaching", cta_text: "Explore Coaching", destination_url: "/group-personal-training-windermere", cta_location: "body" })}
      />

      <FinalCta
        testId="gym-final"
        h2={<>A gym you'll actually <span className="italic text-kore-light">want to walk into.</span></>}
        primary={{ to: JOIN, label: "Join KORE Access", onClick: joinClick("footer") }}
        secondary={{ to: "/contact", label: "Get a Day Pass", onClick: dayPassClick("footer") }}
        note="Independent gym · Bowness-on-Windermere · Windermere · Cumbria"
      />
    </div>
  );
}
